import React, { useRef, useState, useCallback, useEffect } from 'react';
import ScrollLock, { TouchScrollable } from 'react-scrolllock';
import PropTypes from 'prop-types';

import blurStyle from './blur.module.css';

import CleanImage from '../../icons/CleanImage';
import MoveMouse from '../../icons/MoveMouse';

import isMobile from '../../utils/mobileDetecter';
import getMoveEventName from '../../utils/getEventName';

const Blur = (props) => {
  const polygon = useRef(null);

  const [showHint, setShowHint] = useState(true);

  const [imageLoading, setImageLoading] = useState(true);
  const [imageBlur, setImageBlur] = useState(undefined);
  const [ready, setReady] = useState(false);
  const [ctx, setCtx] = useState(null);
  const [currentX, setCurrentX] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  const [prevX, setPrevX] = useState(undefined);
  const [prevY, setPrevY] = useState(undefined);
  const [pointerOn, setPointerOn] = useState(false);

  const handleMouseMove = useCallback((e) => {
    if (!polygon.current) return;

    const pageX = getMoveEventName() === 'touchmove' ? e.touches[0].pageX : e.pageX;
    const pageY = getMoveEventName() === 'touchmove' ? e.touches[0].pageY : e.pageY;

    setShowHint(false);
    setCurrentX(pageX - polygon.current.parentElement.offsetLeft);
    setCurrentY(pageY- polygon.current.parentElement.offsetTop);
  }, [polygon.current])

  const handleErasureStart = (e) => {
    if (!polygon.current) return;

    setCurrentX(e.clientX - polygon.current.parentElement.offsetLeft);
    setCurrentY(e.clientY - polygon.current.parentElement.offsetTop);

    setPrevX(e.clientX - polygon.current.parentElement.offsetLeft);
    setPrevY(e.clientY - polygon.current.parentElement.offsetTop);
    setShowHint(false);

    setPointerOn(true);
  }

  const handleErasureEnd = (e) => {
    setPointerOn(false);
  }

  useEffect(() => {
    setPrevX(currentX);
    setPrevY(currentY);
  }, [currentX, currentY, props.image]);

  useEffect(() => {
    const draw = () => {
      if (!ctx || imageLoading) return;

      ctx.beginPath();
      ctx.fill();
      ctx.moveTo(prevX, prevY);
      ctx.lineTo(currentX, currentY);
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.shadowBlur = 10;
      ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
      ctx.lineWidth = isMobile.any() ? 20 : 30;
      ctx.stroke();
      ctx.closePath();
    }

    draw();
  }, [ctx, currentX, currentY, prevX, prevY, props.image, props.imageBlur]);

  useEffect(() => {
    if (!polygon.current) return;
    const initialize = () => {
      setCtx(polygon.current.getContext('2d'));
    }

    initialize();
  }, [props.image, props.imageBlur, polygon.current])

  useEffect(() => {
    const loadBackground = () => {
      if (!ctx || !polygon.current) return;
      const blurImage = new Image();
      const mainImage = new Image();

      blurImage.onload = () => {
        setImageBlur(blurImage);
      };

      mainImage.onload = () => {
        polygon.current.height = polygon.current.parentElement.clientHeight;
        polygon.current.width = polygon.current.parentElement.clientWidth;
        setImageLoading(false);
      }

      blurImage.src = props.imageBlur;
      mainImage.src = props.image;
    }

    loadBackground();
  }, [props.image, props.imageBlur, props.imageBlur, ctx, polygon.current])

  useEffect(() => {
    if (!imageLoading && imageBlur && ctx && polygon.current && polygon.current.width && polygon.current.height) {
      ctx.drawImage(imageBlur, 0, 0, polygon.current.width, polygon.current.height);
      ctx.globalCompositeOperation = 'destination-out';
      setReady(true);
    }
  }, [imageLoading, imageBlur, ctx, polygon.current])

  useEffect(() => {
    if (pointerOn) {
      polygon.current.addEventListener(getMoveEventName(), handleMouseMove);
    } else {
      polygon.current.removeEventListener(getMoveEventName(), handleMouseMove);
    }
  }, [pointerOn, handleMouseMove])

  useEffect(() => {
    document.body.style.backgroundColor = '#e5e5e5';

    return () => { document.body.style.backgroundColor = '#181818' };
  }, [])

  return (
    <div className={blurStyle.blurContainer} style={{ opacity: ready ? 1 : 0 }}>
      <img className={blurStyle.image} alt="" src={props.image} />

      <TouchScrollable>
        <canvas
          ref={polygon}
          className={blurStyle.blurElement}
          onPointerDown={handleErasureStart}
          onPointerUp={handleErasureEnd}
          onTouchStart={handleErasureStart}
          onTouchEnd={handleErasureEnd}
          onMouseEnter={handleErasureStart}
          onMouseLeave={handleErasureEnd}
          style={{ zIndex: 2 }}
        />
      </TouchScrollable>

      {showHint && ready && (
        <React.Fragment>
          {isMobile.any() && (
            <div className={blurStyle.hint} onMouseDown={() => { setShowHint(false) }}>
              <CleanImage />

              Потрите экран
            </div>
          )}

          {!isMobile.any() && (
            <div className={blurStyle.hint}>
              <MoveMouse />

              Проведите мышкой по фото
            </div>
          )}
        </React.Fragment>
      )}

      {isMobile.any() && (
        <ScrollLock isActive={pointerOn} />
      )}
    </div>
  );
}

Blur.propTypes = {
  image:  PropTypes.string,
  imageBlur: PropTypes.string,
}

Blur.defaultProps = {
  image:  '',
  imageBlur: '',
}

export default Blur;
